import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { RegenLogo } from '@/components/RegenLogo';
import { BarChart3, Smartphone, Star, ArrowRight, Check, X } from 'lucide-react';

const MATRIX = [
  ['Progress Tracking', true, true, true],
  ['Personalized Workout Program', false, true, true],
  ['Personalized Nutrition Plan', false, true, true],
  ['Scan Meals', false, true, true],
  ['Exercise Video Library', 'Browse', 'Personalized', 'Personalized'],
  ['Recovery Tracking', true, true, true],
  ['Progress Analytics', 'Basic', 'Advanced', true],
  ['Program Updates', false, true, true],
  ['Coach Support', false, false, true],
];

export default function ChooseJourney() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const choose = async (tier) => {
    setBusy(true);
    try {
      await api.post('/journey/select', { tier });
      if (tier === 'regen_method') { nav('/enroll/regen-method'); return; }
      await refresh();
      if (tier === 'regen_plus') toast.success('Your 7-day YourReGen+ free trial has started!');
      nav('/tracker-onboarding');
    } catch { toast.error('Something went wrong'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-elev2)] text-[var(--c-ink)] px-6 py-10" data-testid="choose-journey"
         style={{ paddingTop: 'max(env(safe-area-inset-top),2.5rem)' }}>
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-4"><RegenLogo size="md" /></div>
        <h1 className="font-display font-black text-3xl text-center tracking-tight">Choose Your Journey</h1>
        <p className="text-[var(--c-ink-soft)] text-sm text-center mt-2">Pick how you want to use YourReGen. You can upgrade anytime.</p>

        <div className="space-y-4 mt-8">
          <JourneyCard testid="journey-method" color='#65A30D' icon={Star} badge="Best results"
            title="YourReGen Protocol" sub="Your complete transformation with Coach JP: personalized training, nutrition, accountability and ongoing program updates."
            onClick={() => choose('regen_method')} disabled={busy} cta="Start with Coach JP" />
          <JourneyCard testid="journey-plus" color="#00C8FF" icon={Smartphone} badge="7-day free trial"
            title="YourReGen+" sub="Personalized workout & nutrition plans, Scan Meals, recovery tools and premium tracking."
            onClick={() => choose('regen_plus')} disabled={busy} cta="Start 7-day free trial" />
          <JourneyCard testid="journey-tracker" color="#7DD3FC" icon={BarChart3}
            title="YourReGen Free" sub="Free. Track your weight, meals, recovery and progress."
            onClick={() => choose('tracker')} disabled={busy} cta="Start free" />
        </div>

        {/* Comparison */}
        <div className="mt-8 rounded-2xl border border-[var(--c-hair)] overflow-hidden">
          <div className="grid grid-cols-4 text-[10px] font-bold uppercase tracking-wider bg-[var(--c-film)]">
            <div className="p-2.5 text-[var(--c-ink-soft)]">Feature</div>
            <div className="p-2.5 text-center text-[#7DD3FC]">YourReGen Free</div>
            <div className="p-2.5 text-center text-[#00C8FF]">YourReGen+</div>
            <div className="p-2.5 text-center text-[var(--c-green-ink)]">Method</div>
          </div>
          {MATRIX.map((row, i) => (
            <div key={i} className="grid grid-cols-4 text-xs border-t border-[var(--c-hair)]">
              <div className="p-2.5 text-[var(--c-ink-soft)]">{row[0]}</div>
              {row.slice(1).map((v, j) => <Cell key={j} v={v} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Cell({ v }) {
  if (v === true) return <div className="p-2.5 flex justify-center"><Check className="w-4 h-4 text-[var(--c-green-ink)]" /></div>;
  if (v === false) return <div className="p-2.5 flex justify-center"><X className="w-4 h-4 text-[var(--c-ink-soft)]" /></div>;
  return <div className="p-2.5 text-center text-[10px] text-[var(--c-ink-soft)]">{v}</div>;
}

function JourneyCard({ testid, color, icon: Icon, title, sub, cta, badge, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className="w-full text-left rounded-2xl border p-5 transition active:scale-[0.99] relative overflow-hidden"
            style={{ borderColor: `${color}55`, background: `${color}0d` }}>
      {badge && <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: color, color: '#04203f' }}>{badge}</span>}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}22` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-black text-lg" style={{ color }}>{title}</div>
          <div className="text-xs text-[var(--c-ink-soft)] leading-snug">{sub}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="font-semibold text-sm inline-flex items-center gap-1.5" style={{ color }}>{cta} <ArrowRight className="w-4 h-4" /></span>
      </div>
    </button>
  );
}
